import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Badge, Progress, Table, Tooltip } from 'flowbite-react';
import { ChangeTrack } from '../ChangeTrack';
import { ClientReplyPendingBadge } from '../ClientReplyPendingBadge';
import { Icon } from '../Icon';
import { UserAvatar } from '../UserAvatar';
import { Change, Track, User } from '../../models';
import { formatMomentDate } from '../../locales';
import {
    handleChangeMiddleClick,
    preventMiddleClickAutoscroll,
} from '../../utils/openChangeInNewBrowserTab';

export interface ChangeListHeadProps {
    hideUser?: boolean;
    hideStatut?: boolean;
    leadingColumn?: boolean;
    projectsIds: string[];
}

export function ChangeListHead(props: ChangeListHeadProps) {
    const { t } = useTranslation();
    const { hideUser, hideStatut, leadingColumn, projectsIds } = props;

    return (
        <Table.Head>
            {leadingColumn && (
                <Table.HeadCell className="w-10 min-w-[2.5rem] max-w-[2.5rem] p-2" />
            )}
            <Table.HeadCell>{t('changes.change')}</Table.HeadCell>
            {projectsIds.length > 1 && (
                <Table.HeadCell>{t('projects.project')}</Table.HeadCell>
            )}
            {!hideUser && (
                <Table.HeadCell></Table.HeadCell>
            )}
            <Table.HeadCell>
                {!hideStatut && (
                    t('changes.status')
                )}
            </Table.HeadCell>
            <Table.HeadCell>{t('changes.estimate')}</Table.HeadCell>
            <Table.HeadCell>{t('changes.deadline')}</Table.HeadCell>
            <Table.HeadCell>{t('changes.importance')}</Table.HeadCell>
            <Table.HeadCell>{t('changes.priority')}</Table.HeadCell>
        </Table.Head>
    );
}

export interface ChangeListRowProps {
    change: Change;
    hideUser?: boolean;
    hideStatut?: boolean;
    projectsIds: string[];
    user?: User;
    active?: Track;
    onSelectChange: (change?: Change) => void;
    leadingCell?: React.ReactNode;
    rowClassName?: string;
    rowProps?: React.HTMLAttributes<HTMLTableRowElement>;
}

function changeIsReview(change: Change) {
    return change?.progress?.status === Change.STATUS_TEAM_REVIEW ||
        change?.progress?.status === Change.STATUS_CLIENT_REVIEW;
}

function changeIsFinish(change: Change) {
    return change?.progress?.status === Change.STATUS_FINISHED ||
        change?.progress?.status === Change.STATUS_DEPLOYED ||
        change?.progress?.status === Change.STATUS_PENDING_DEPLOYMENT;
}

export const ChangeListRow = React.forwardRef<HTMLTableRowElement, ChangeListRowProps>(
    function ChangeListRow(props, ref) {
        const { t, i18n } = useTranslation();
        const {
            change,
            hideUser,
            hideStatut,
            projectsIds,
            user,
            active,
            onSelectChange,
            leadingCell,
            rowClassName = '',
            rowProps,
        } = props;

        const progression = Math.round(change.progression());
        const { className: rowPropsClassName, onClick: rowPropsOnClick, ...restRowProps } = rowProps || {};

        return (
            <tr
                ref={ref}
                {...restRowProps}
                className={[
                    'cursor-pointer border-t bg-white dark:bg-gray-800',
                    changeIsReview(change) ? '!bg-blue-50 dark:!bg-blue-950/40 border-blue-200 dark:border-blue-800' : '',
                    changeIsFinish(change) ? '!bg-green-50 dark:!bg-green-950/30 border-green-200 dark:border-green-800' : '',
                    rowPropsClassName,
                    rowClassName,
                ].filter(Boolean).join(' ')}
                onClick={(e) => {
                    rowPropsOnClick?.(e);
                    if (!e.defaultPrevented) onSelectChange(change);
                }}
                onMouseDown={preventMiddleClickAutoscroll}
                onAuxClick={(e) => handleChangeMiddleClick(e, change)}
            >
                {leadingCell && (
                    <td className="w-10 min-w-[2.5rem] max-w-[2.5rem] align-middle p-2">
                        {leadingCell}
                    </td>
                )}
                <td className="min-w-0 px-6 py-4">
                    <div className="flex items-start space-x-2">
                        <div className="flex-col space-y-1 min-w-0">
                            <h4 className="font-medium heading-md text-gray-900 dark:text-gray-100">
                                {change.title.fr || ''}
                            </h4>
                            <div className="flex flex-wrap gap-x-2 gap-y-1">
                                <Badge color={change.color()} className="w-fit text-xs px-1">
                                    <Icon type={change.iconName()} size={3} className="inline" />&nbsp;
                                    {change.slugName()}
                                </Badge>
                                {(change.nbComments > 0) && (
                                    <Badge color="indigo" className="w-fit text-xs px-1">
                                        <Icon type="comment" className="inline" />&nbsp;
                                        {change.nbComments}
                                    </Badge>
                                )}
                                {change.nbAttachments > 0 && (
                                    <Badge color="gray" className="w-fit text-xs px-1">
                                        <Icon type="file" className="inline" />&nbsp;
                                        {change.nbAttachments}
                                    </Badge>
                                )}
                                {change.hasTechnicalContext() && (
                                    <Badge color="indigo" className="w-fit text-xs px-1">
                                        <Icon type="notes" className="inline" />
                                    </Badge>
                                )}
                                <ClientReplyPendingBadge change={change} dismissable />
                                {change.quote && (
                                    <Badge color="blue" className="w-fit text-xs px-1">
                                        <Icon type="law" className="inline" />&nbsp;
                                        {change.quote.slug} | {t(`quotes.status_type.${change.quote.status}`)}
                                    </Badge>
                                )}
                                {change.release && (
                                    <Badge color="indigo" size="sm" className="w-fit inline-flex items-center gap-1.5 px-1.5">
                                        <Icon type="release" color="indigo" className="inline shrink-0"/>
                                        {change.release.version}
                                    </Badge>
                                )}
                                {(change.commits?.length > 0) && (
                                    <div className="flex items-center space-x-1">
                                        {change.commits?.map(commit => (
                                            <Badge key={commit.hash} color="success" className="w-fit px-1">
                                                <Icon type="commit" size={3} className="inline"/>&nbsp;
                                                {commit.message}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </td>
                {projectsIds.length > 1 && (
                    <td className="px-6 py-4">
                        <span className="font-medium">{change.project?.name}</span>
                    </td>
                )}
                {!hideUser && (
                    <td className="px-6 py-4">
                        {change.assignee && !change.isTimeTrackable(user) && (
                            <UserAvatar size={6} user={change.assignee} noTooltip className="my-1" />
                        )}
                        {change.assignee && change.isTimeTrackable(user) && (
                            <div className="group">
                                <div className="h-6 w-6 flex justify-center items-center">
                                    {active?.change?._id === change._id ? (
                                        <ChangeTrack className="group-hover:hidden" change={change} />
                                    ) : (
                                        <UserAvatar size={6} user={change.assignee} noTooltip className="group-hover:hidden my-1"/>
                                    )}
                                    <ChangeTrack className="hidden group-hover:block" change={change} />
                                </div>
                            </div>
                        )}
                        {!change.assignee && (
                            <div className="h-6 w-6"></div>
                        )}
                    </td>
                )}
                <td className="px-6 py-4">
                    {!hideStatut && (
                        <div className="flex-col space-y-1">
                            <div className="text-xs font-light border border-gray-100 dark:border-gray-700 rounded px-2 whitespace-nowrap flex flex-row justify-between">
                                <span className={`whitespace-nowrap text-ellipsis overflow-hidden truncate inline-block ${change.estimate > 0 ? 'max-w-[9rem]' : ''}`}>
                                    <Icon type="progress" size={3} className="inline mr-1"/>&nbsp;
                                    {t(change.progressStatusLabelKey())}
                                </span>
                            </div>
                            <Tooltip content={progression + '%'} placement="top">
                                <Progress
                                    progress={progression}
                                    size="sm"
                                    color={ progression === 100 ? 'green' : (progression > 20 ? 'cyan' : 'yellow')}
                                />
                            </Tooltip>
                        </div>
                    )}
                    {hideStatut && changeIsReview(change) && (
                        <Icon type="eye" color="blue-600" size={6} />
                    )}
                </td>
                <td className="px-6 py-4">
                    { change.estimate > 0 && (
                        <Badge color="warning" className="w-fit whitespace-nowrap px-1">
                            <>
                                <Icon type="estimated" size={3} className="inline mr-1"/>&nbsp;
                                {change?.estimate} {t('changes.estimate_units_min')}
                            </>
                        </Badge>
                    )}
                </td>
                <td className="px-6 py-4">
                    <div className="flex-col space-y-1">
                        {change.deadline && (
                            <Badge color={change.deadline.isBefore(moment().startOf('day')) ? 'pink' : (change.deadline.isAfter(moment()) ? 'green' : 'indigo')} size="xs" className="w-fit">
                                <span className="font-normal">
                                    {formatMomentDate(change.deadline, 'L')}
                                </span>
                            </Badge>
                        )}
                        {(change.sprints?.length > 0) && (
                            <Badge color="warning" size="xs" className="w-fit px-1">
                                <span className="text-xs font-normal">
                                    <Icon type="sprint" color="indigo" className="inline mr-1.5"/>
                                    {change.sprints.slice(-1)[0].name}
                                </span>
                            </Badge>
                        )}
                    </div>
                </td>
                <td className="px-6 py-4">
                    {t(`changes.importance_value_${change.importance}`)}
                </td>
                <td className="px-6 py-4">
                    <Badge color="indigo" className="w-fit" size="xs">
                        <Icon type="priority" className="inline mr-1" />
                        {change.priority}
                    </Badge>
                </td>
            </tr>
        );
    }
);
